/**
 * /blog/v2/:articleId — legacy permalink.
 *
 * The v2 engine now lives entirely on the topic workspace (/blog/topic/:id):
 * generation, evidence core, control center, images and edit all happen there.
 * So this route no longer renders its own studio — it RESOLVES the article to
 * its owning topic and redirects into the workspace. Orphan legacy articles
 * (no owning blog_topic) fall back to a read-only view with a dashboard link,
 * so old links never dead-end.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../api";
import Md from "../components/Md";

interface DetailResponse {
  id: string;
  title: string | null;
  topic: string;
  body_md: string;
  benefit_area_en?: string | null;
  pillar: string;
  topic_id?: string | null;
}

export default function BlogV2ArticleDetailPage() {
  const { articleId } = useParams<{ articleId: string }>();
  const [article, setArticle] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!articleId) return;
      setLoading(true);
      try {
        const d = await api.get<DetailResponse>(`/api/blog/v2/articles/${articleId}`);
        if (!cancelled) setArticle(d);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-text-3">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading article …
      </div>
    );
  }

  // Owned by a topic → the workspace is the single source of truth.
  if (article?.topic_id) {
    return <Navigate to={`/blog/topic/${article.topic_id}`} replace />;
  }

  // Orphan legacy article — read-only fallback.
  return (
    <div className="relative max-w-[820px] mx-auto px-4 md:px-6 py-6">
      <Link to="/blog/dashboard" className="inline-flex items-center gap-1.5 text-sm text-text-3 hover:text-text">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>
      {article ? (
        <article className="mt-5 rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50/40 dark:bg-ink-800/40 p-5 md:p-7">
          <div className="max-w-[72ch] [&_img]:max-w-full [&_img]:rounded-xl [&_img]:my-3">
            <div className="mb-2 text-xs uppercase tracking-wider text-text-3">
              {article.benefit_area_en || article.pillar} · legacy article
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink mb-4">
              {article.title || article.topic}
            </h1>
            <Md>{article.body_md}</Md>
          </div>
        </article>
      ) : (
        <div className="mt-6 text-sm text-text-3">{error || "Article not found."}</div>
      )}
    </div>
  );
}
